import React, { createContext, useContext } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';

interface AuthContextType {
  user: any | null;
  session: any | null;
  loading: boolean;
  userId: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded } = useUser();
  const clerk = useClerk();

  const signIn = async (email: string, password: string) => {
    try {
      const result = await clerk.client.signIn.create({
        identifier: email,
        password,
      });
      
      if (result.status === 'complete') {
        await clerk.setActive({ session: result.createdSessionId });
      }
    } catch (error: any) {
      throw new Error(error.errors?.[0]?.message || 'Failed to sign in');
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const result = await clerk.client.signUp.create({
        emailAddress: email,
        password,
      });
      
      if (result.status === 'complete') {
        await clerk.setActive({ session: result.createdSessionId });
      }
    } catch (error: any) {
      throw new Error(error.errors?.[0]?.message || 'Failed to sign up');
    }
  };

  const signOut = async () => {
    try {
      await clerk.signOut();
      // Clerk will automatically redirect to afterSignOutUrl if configured
    } catch (error: any) {
      throw new Error(error.errors?.[0]?.message || 'Failed to sign out');
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user,
      session: user ? { user } : null,
      loading: !isLoaded,
      userId: user?.id || null,
      signIn,
      signUp,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}